import React, { useState, useEffect } from 'react';
import './FilterMenu.css';

const FilterMenu = ({ isOpen, onClose, onFilterChange }) => {
  const [tempFilters, setTempFilters] = useState({
    category: 'all',
    priceRange: { min: '', max: '' },
    sortBy: 'newest'
  });

  const categories = [
    { id: 'all', name: 'All Products', icon: '📦' },
    { id: 'electronics', name: 'Electronics', icon: '📱' },
    { id: 'clothing', name: 'Clothing', icon: '👕' },
    { id: 'home', name: 'Home & Living', icon: '🏠' },
    { id: 'beauty', name: 'Beauty & Personal Care', icon: '💄' },
    { id: 'sports', name: 'Sports & Outdoors', icon: '⚽' },
    { id: 'books', name: 'Books & Media', icon: '📚' },
    { id: 'toys', name: 'Toys & Games', icon: '🎮' },
    { id: 'jewelry', name: 'Jewelry & Accessories', icon: '💍' },
    { id: 'automotive', name: 'Automotive', icon: '🚗' },
    { id: 'garden', name: 'Garden & Outdoor', icon: '🌺' },
    { id: 'health', name: 'Health & Wellness', icon: '💊' },
    { id: 'food', name: 'Food & Beverages', icon: '🍎' },
    { id: 'pets', name: 'Pet Supplies', icon: '🐾' },
    { id: 'office', name: 'Office Supplies', icon: '📎' },
    { id: 'art', name: 'Arts & Crafts', icon: '🎨' },
    { id: 'music', name: 'Musical Instruments', icon: '🎸' },
    { id: 'travel', name: 'Travel & Luggage', icon: '✈️' },
    { id: 'baby', name: 'Baby & Kids', icon: '👶' },
    { id: 'party', name: 'Party Supplies', icon: '🎉' },
    { id: 'fitness', name: 'Fitness & Exercise', icon: '💪' },
    { id: 'camera', name: 'Cameras & Photography', icon: '📸' },
    { id: 'tools', name: 'Tools & Hardware', icon: '🔧' },
    { id: 'stationery', name: 'Stationery', icon: '✏️' }
  ];

  // Handle body class for menu open state
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isOpen]);

  // Handle clicks outside the menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      const menuContent = document.querySelector('.filter-menu__content');
      if (isOpen && menuContent && !menuContent.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Reset filters when menu opens
  useEffect(() => {
    if (isOpen) {
      setTempFilters({
        category: 'all',
        priceRange: { min: '', max: '' },
        sortBy: 'newest'
      });
    }
  }, [isOpen]);

  const handleCategoryChange = (category) => {
    setTempFilters(prev => ({
      ...prev,
      category
    }));
  };

  const handlePriceChange = (type, value) => {
    const numValue = value === '' ? '' : Number(value);
    setTempFilters(prev => ({
      ...prev,
      priceRange: {
        ...prev.priceRange,
        [type]: numValue
      }
    }));
  };

  const handleSortChange = (sortBy) => {
    setTempFilters(prev => ({
      ...prev,
      sortBy
    }));
  };

  const handleReset = () => {
    setTempFilters({
      category: 'all',
      priceRange: { min: '', max: '' },
      sortBy: 'newest'
    });
    onFilterChange({
      category: 'all',
      priceRange: { min: '', max: '' },
      sortBy: 'newest'
    });
  };

  const handleApply = () => {
    onFilterChange(tempFilters);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="filter-menu">
      <div 
        className={`filter-menu__overlay ${isOpen ? 'open' : ''}`}
        aria-hidden={!isOpen}
        role="presentation"
      />

      <div 
        className={`filter-menu__content ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Меню фільтрів"
      >
        <div className="filter-menu__header">
          <div className="filter-menu__header-content">
            <h2>Фільтри</h2>
            <p className="filter-menu__subtitle">Уточніть пошук</p>
          </div>
          <button 
            className="filter-menu__close"
            onClick={onClose}
            aria-label="Закрити фільтри"
          >
            ×
          </button>
        </div>

        <div className="filter-menu__body">
          <div className="filter-menu__section">
            <div className="filter-menu__section-header">
              <h3>Категорії</h3>
              <span className="filter-menu__section-count">{categories.length}</span>
            </div>
            <div className="filter-menu__categories">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`filter-menu__category ${tempFilters.category === category.id ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category.id)}
                  aria-pressed={tempFilters.category === category.id}
                >
                  <span className="filter-menu__category-icon">{category.icon}</span>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="filter-menu__section">
            <div className="filter-menu__section-header">
              <h3>Ціна</h3>
            </div>
            <div className="filter-menu__price-range">
              <div className="filter-menu__price-input">
                <input
                  type="number"
                  placeholder="Від"
                  value={tempFilters.priceRange.min}
                  onChange={(e) => handlePriceChange('min', e.target.value)}
                  min="0"
                />
                <span className="filter-menu__price-currency">$</span>
              </div>
              <div className="filter-menu__price-separator" />
              <div className="filter-menu__price-input">
                <input
                  type="number"
                  placeholder="До"
                  value={tempFilters.priceRange.max}
                  onChange={(e) => handlePriceChange('max', e.target.value)}
                  min="0"
                />
                <span className="filter-menu__price-currency">$</span>
              </div>
            </div>
          </div>

          <div className="filter-menu__section">
            <div className="filter-menu__section-header">
              <h3>Сортування</h3>
            </div>
            <div className="filter-menu__sort-options">
              <button
                className={`filter-menu__sort-option ${tempFilters.sortBy === 'newest' ? 'active' : ''}`}
                onClick={() => handleSortChange('newest')}
                aria-pressed={tempFilters.sortBy === 'newest'}
              >
                <span className="filter-menu__sort-icon">🕒</span>
                <span>Спочатку нові</span>
              </button>
              <button
                className={`filter-menu__sort-option ${tempFilters.sortBy === 'price-low' ? 'active' : ''}`}
                onClick={() => handleSortChange('price-low')}
                aria-pressed={tempFilters.sortBy === 'price-low'}
              >
                <span className="filter-menu__sort-icon">💰</span>
                <span>Ціна: від найнижчої</span>
              </button>
              <button
                className={`filter-menu__sort-option ${tempFilters.sortBy === 'price-high' ? 'active' : ''}`}
                onClick={() => handleSortChange('price-high')}
                aria-pressed={tempFilters.sortBy === 'price-high'}
              >
                <span className="filter-menu__sort-icon">💰</span>
                <span>Ціна: від найвищої</span>
              </button>
              <button
                className={`filter-menu__sort-option ${tempFilters.sortBy === 'popular' ? 'active' : ''}`}
                onClick={() => handleSortChange('popular')}
                aria-pressed={tempFilters.sortBy === 'popular'}
              >
                <span className="filter-menu__sort-icon">🔥</span>
                <span>Найпопулярніші</span>
              </button>
            </div>
          </div>
        </div>

        <div className="filter-menu__footer">
          <button 
            className="filter-menu__reset"
            onClick={handleReset}
          >
            Reset
          </button>
          <button 
            className="filter-menu__apply"
            onClick={handleApply}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterMenu; 